/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the drug management
 * opms extension.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { IOnlinePatientManagementExtension, OnlinePatientManagement } from '@ngt/opms';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as React from 'react';
import CambriaContext from './contexts/data/CambriaContext';
import registerScreenFailuresReducer from './store/modules/data/screenFailures';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface ICambriaExtensionReducerOptions {
    screenFailures?: boolean | null;
};

interface ICambriaStoreOptions {
    initialiseReducers?: boolean | null | ICambriaExtensionReducerOptions;
};

export interface ICambriaExtensionOptions {
    /**
     * The configuration options to use when creating the redux store.
     *
     * (Defaults used if not provided).
     */
    storeOptions?: ICambriaStoreOptions | null;
}

/*
 * ---------------------------------------------------------------------------------
 * Classes
 * ---------------------------------------------------------------------------------
 */

/**
 * This class handles the global context used by the OPMS.
 */
export class CambriaExtension implements IOnlinePatientManagementExtension {
    // @ts-expect-error
    private reducerOptions: boolean | ICambriaExtensionReducerOptions;
    public ProviderComponent: React.ComponentType<{
        children: React.ReactNode;
    }> | undefined;

    /**
     * Creates a new OnlinePatientManagement using the provided configuration options.
     * @param options Configuration Options
     */
    constructor(options: ICambriaExtensionOptions) {
        this.initialiseReducerOptions(options?.storeOptions?.initialiseReducers);
        this.initialiseProviderComponent(options);
    }

    private initialiseReducerOptions(reducerOptions?: boolean | ICambriaExtensionReducerOptions | null) {
        this.reducerOptions = reducerOptions ?? true;
    }

    private initialiseProviderComponent(_options: ICambriaExtensionOptions) {
        const context = {}

        this.ProviderComponent = ({ children }) => {
            return (
                <CambriaContext.Provider value={context}>
                    {children}
                </CambriaContext.Provider>
            );
        }
    }


    /**
     * This method initialises the Reducers in the Reducer Registry.
     * @param onlinePatientManagement Online Patient Management.
     */
    public initialiseReducers(opms: OnlinePatientManagement) {
        // Check if reducer initialisation was requested to be skipped.
        if (this.reducerOptions === false) {
            return;
        }

        if (this.reducerOptions === true || this.reducerOptions?.screenFailures !== false) {
            // Register Screen Failure Reducer if requested.
            registerScreenFailuresReducer(opms.serviceStackClient, opms.reducerRegistry);
        }
    }
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default CambriaExtension;